import * as THREE from 'three';
import { emis, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Caparazonte (era V, docs/CRIATURAS.md «Era V · Somnhold»): a piece of the Tortoise's own shell, broken off and
 * dreamt into walking. Two hexes long, the biggest of the common nightmares: a high dome of hexagonal plates of
 * grey rock with moss in the seams and hanging off the rim, a ring of serrated marginal plates round the bottom,
 * four short legs like pillars of the titan's stone, and at the front a blunt prow of plates over a low head that
 * rams. Small nightmares ride its back, clinging to the plates (two trasgos and two skeletons, ink-black and
 * bone-pale, violet eyes): it carries them up to the walls and they climb down in turns as it breaks.
 *
 * Bones: body (the dome, all its plates and moss), head (the prow and the head under it), four legs (legFL, legFR,
 * legBL, legBR, one piece each, from the hip under the rim to the foot) and the riders rider1…rider4 (each pivots at
 * the hold it grips: the shake clip flings them off and they climb back out of the seams). Faces +Z, its right on +X.
 * Opaque shared materials; the riders' eyes and the glow in the seams of the prow the one self-lit material, plus
 * glow sprites (no lights).
 */

export const SHELLBACK_HEIGHT = 2.35;
/** Where the riders hold on (model units, on the dome), and which nightmare each is. */
export const SHELLBACK_RIDERS: readonly { at: readonly [number, number, number]; kind: 'goblin' | 'skeleton'; face: number }[] = [
  { at: [0.42, 1.78, 0.42], kind: 'goblin', face: 0.5 },
  { at: [-0.5, 1.7, -0.2], kind: 'skeleton', face: -0.9 },
  { at: [0.2, 1.62, -0.78], kind: 'goblin', face: 2.6 },
  { at: [-0.3, 1.86, 0.62], kind: 'skeleton', face: -0.2 },
];

/** The dome's surface: an ellipsoid (half-axes a across, b up, c long) resting on y = y0. */
const DOME = { a: 1.05, b: 1.02, c: 1.42, y0: 0.74 };
const domeAt = (u: number, v: number): { p: THREE.Vector3; n: THREE.Vector3 } => {
  // u round the dome (0 = front), v up from the rim (0) to the top (π/2).
  const x = DOME.a * Math.cos(v) * Math.sin(u), z = DOME.c * Math.cos(v) * Math.cos(u), y = DOME.y0 + DOME.b * Math.sin(v);
  const n = V(x / (DOME.a * DOME.a), (y - DOME.y0) / (DOME.b * DOME.b), z / (DOME.c * DOME.c)).normalize();
  return { p: V(x, y, z), n };
};

const UP = V(0, 1, 0);

export const shellback = (o?: BaseOpts): Model => {
  const c = begin(o, 1.2, 7303);
  const { g, rng } = c;
  const k4 = kit(rng);

  // The shell's grey rock (the Tortoise's, as the shell serpent's), its lighter crowns, the dark rock of the seams
  // and legs, the moss; the riders' ink and bone; the violet of their eyes and the dim fire in the prow.
  const plate = stone('#5d6163', { bumpScale: 0.04 });
  const plateL = stone('#8a8f90');
  const rock = stone('#2a2e31', { bumpScale: 0.05 });
  const moss = std('#4a5a2e', { roughness: 1, flatShading: true, bumpScale: 0.05 });
  const ink = std('#0e0c13', { roughness: 0.55, metalness: 0.2 });
  const bone = std('#b9b7c2', { roughness: 0.7 });
  const eye = emis('#e6d4ff', '#9b4dff', 2.4);

  /** A hex scute on the dome at (u, v): a low six-sided slab along the surface normal. */
  const scute = (parent: THREE.Object3D, mat: THREE.Material, u: number, v: number, r: number, h: number, lift = 0): THREE.Mesh => {
    const { p, n } = domeAt(u, v);
    const m = M(new THREE.CylinderGeometry(r * 0.82, r, h, 6), mat, parent);
    m.position.copy(p).addScaledVector(n, h * 0.35 + lift);
    m.quaternion.setFromUnitVectors(UP, n);
    m.rotateY(u * 0.7);
    return m;
  };

  /* ---------- the dome ---------- */
  const bodyB = joint(g, V(0, 0.9, 0), 'body');
  const body = bodyB.abs;
  // The core of dark rock the plates sit on.
  const core = M(new THREE.SphereGeometry(1, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), rock, body);
  core.position.set(0, DOME.y0, 0);
  core.scale.set(DOME.a * 0.97, DOME.b * 0.97, DOME.c * 0.97);
  // The belly: a flat underside of rock.
  const belly = M(new THREE.CylinderGeometry(1, 0.92, 0.22, 12), rock, body);
  belly.position.set(0, DOME.y0 - 0.08, 0);
  belly.scale.set(DOME.a * 0.95, 1, DOME.c * 0.95);
  // The crown plate on top, then rings of hex plates down to the rim, the light ones crowning the central row.
  scute(body, plateL, 0, Math.PI / 2, 0.36, 0.14);
  const rings: [number, number, number][] = [[1.18, 6, 0.33], [0.82, 10, 0.3], [0.45, 13, 0.27]];
  rings.forEach(([v, n, r], ri) => {
    for (let i = 0; i < n; i++) {
      const u = (i / n) * Math.PI * 2 + ri * 0.3;
      const mat = (ri === 0 && i % 2 === 0) || (ri === 1 && i % 3 === 0) ? plateL : plate;
      scute(body, mat, u, v, r * (0.95 + rng() * 0.12), 0.12 + rng() * 0.04, (rng() - 0.5) * 0.03);
    }
  });
  // The rim: serrated marginal plates curving down over the legs; at the back the edge is broken off, a jagged
  // break of dark rock (it is a piece of the shell).
  for (let i = 0; i < 18; i++) {
    const u = (i / 18) * Math.PI * 2 + 0.17;
    const { p, n } = domeAt(u, 0.1);
    const out = V(n.x, 0, n.z).normalize();
    const back = Math.cos(u) < -0.75;
    if (back) {
      for (let j = 0; j < 2; j++) k4.shard(body, j ? plate : rock, p.clone().add(V(0, 0.1 + j * 0.25, 0)), out.clone().add(V((rng() - 0.5) * 0.6, 0.4 + rng() * 0.6, 0)), 0.3 + rng() * 0.25, 0.1);
      continue;
    }
    const m = M(new THREE.BoxGeometry(0.36, 0.09, 0.26), i % 4 === 0 ? plateL : plate, body);
    m.position.copy(p).addScaledVector(out, 0.04).add(V(0, -0.1, 0));
    m.quaternion.setFromUnitVectors(V(0, 0, 1), out);
    m.rotateX(1.0);
    if (i % 2 === 0) k4.shard(body, rock, p.clone().addScaledVector(out, 0.1).add(V(0, -0.22, 0)), out.clone().add(V(0, -1.4, 0)), 0.16, 0.05);
  }
  // Moss in the seams, mats of it on the shoulders of the dome, and hanging off the rim in beards.
  for (let i = 0; i < 14; i++) {
    const u = rng() * 6.3, v = 0.3 + rng() * 1.0;
    const { p, n } = domeAt(u, v);
    const m = k4.lump(body, moss, p.x, p.y, p.z, 0.18 + rng() * 0.1, 0.05, 0.16 + rng() * 0.08, 0.3, 0);
    m.quaternion.setFromUnitVectors(UP, n);
  }
  for (let i = 0; i < 8; i++) {
    const u = (i / 8) * Math.PI * 2 + rng() * 0.4;
    const { p, n } = domeAt(u, 0.02);
    const out = V(n.x, 0, n.z).normalize();
    const top = p.clone().addScaledVector(out, 0.2).add(V(0, -0.06, 0));
    const len = 0.25 + rng() * 0.25;
    k4.tube(body, [top, top.clone().add(V(out.x * 0.05, -len * 0.5, out.z * 0.05)), top.clone().add(V(out.x * 0.02, -len, out.z * 0.02))], 0.035, moss, 4);
  }
  // A few cracks across the dome, the dark showing through, and old scars where plates broke off.
  for (let i = 0; i < 3; i++) {
    const u0 = rng() * 6.3;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 4; j++) {
      const { p, n } = domeAt(u0 + j * 0.18, 1.3 - j * 0.3);
      pts.push(p.addScaledVector(n, 0.13));
    }
    k4.crack(body, pts, 0.022, ink, 0.05);
  }

  /* ---------- the prow and the head: a blunt ram of plates, a low head under it with a dim glow ---------- */
  const headB = joint(body, V(0, 0.9, 1.25), 'head');
  const head = G(headB.abs, 0, 0, 0.32);
  k4.lump(head, rock, 0, 0.75, 1.32, 0.42, 0.3, 0.38, 0.3, 1, 0.15);
  // The ram: three heavy plates over the snout, the middle one jutting.
  for (const [x, y, z, s] of [[0, 0.98, 1.48, 0.3], [-0.27, 0.86, 1.4, 0.22], [0.27, 0.86, 1.4, 0.22]] as const) {
    const m = M(new THREE.CylinderGeometry(s * 0.85, s, 0.14, 6), x === 0 ? plateL : plate, head);
    m.position.set(x, y, z);
    m.quaternion.setFromUnitVectors(UP, V(x * 1.5, 0.6, 1).normalize());
  }
  k4.lump(head, plate, 0, 0.62, 1.58, 0.28, 0.16, 0.22, 0.3, 0);
  // Two horns of rock raking forward, for the ram.
  for (const k of [-1, 1]) k4.shard(head, plateL, V(k * 0.2, 0.7, 1.6), V(k * 0.25, 0.25, 1), 0.38, 0.07);
  // The eyes: slits of violet in the dark under the prow, and the dim fire of the nightmare in the seams.
  for (const k of [-1, 1]) {
    const e = M(new THREE.BoxGeometry(0.1, 0.025, 0.02), eye, head);
    e.position.set(k * 0.17, 0.78, 1.69);
    e.rotation.z = k * -0.25;
    glowSprite(head, '#b48cff', 0.28, V(k * 0.17, 0.78, 1.72), 0.9);
  }
  glowSprite(head, '#7f4dd8', 0.9, V(0, 0.62, 1.6), 0.28);

  /* ---------- legs: short pillars of the titan's stone, cuffed in plate, toes of rock ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const [kz, e] of [[1, 'F'], [-1, 'B']] as const) {
    for (const k of [-1, 1]) {
      const s = k < 0 ? 'L' : 'R';
      // Out from under the rim at the four corners, splayed like a tortoise's.
      const hip = V(k * 0.66, 0.86, kz * 0.7);
      const foot = V(k * 1.02, 0.08, kz * 1.05);
      const legB = joint(g, hip, `leg${e}${s}`);
      const L = legB.abs;
      const col = limb(L, hip, foot, 0.32, 0.28, rock, 7);
      col.rotateY(rng() * 6);
      // Scales of plate down the leg, a cuff at the ankle, and a shoulder plate where it leaves the shell.
      const mid = hip.clone().lerp(foot, 0.45);
      const outward = V(k, 0.3, kz * 0.6).normalize();
      for (let i = 0; i < 3; i++) {
        const at = hip.clone().lerp(foot, 0.25 + i * 0.22).addScaledVector(outward, 0.26 - i * 0.02);
        const m = M(new THREE.CylinderGeometry(0.13, 0.15, 0.07, 6), i === 1 ? plateL : plate, L);
        m.position.copy(at);
        m.quaternion.setFromUnitVectors(UP, outward);
      }
      k4.lump(L, plate, mid.x, mid.y + 0.2, mid.z, 0.3, 0.12, 0.3, 0.3, 1);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.4;
        const m = M(new THREE.CylinderGeometry(0.12, 0.14, 0.07, 6), plate, L);
        m.position.set(foot.x + Math.cos(a) * 0.29, 0.26, foot.z + Math.sin(a) * 0.29);
        m.quaternion.setFromUnitVectors(UP, V(Math.cos(a), 0.3, Math.sin(a)).normalize());
      }
      k4.lump(L, rock, foot.x, 0.1, foot.z + kz * 0.06, 0.36, 0.14, 0.38, 0.3, 1);
      for (let i = -1; i <= 1; i++) k4.shard(L, plateL, V(foot.x + i * 0.14, 0.08, foot.z + kz * 0.3), V(i * 0.3, -0.2, kz), 0.16, 0.055);
      legs[`leg${e}${s}`] = legB.p;
    }
  }

  /* ---------- the riders: small nightmares clinging to the plates ---------- */
  const riders: Record<string, THREE.Object3D> = {};
  SHELLBACK_RIDERS.forEach((r, i) => {
    const at = V(r.at[0], r.at[1], r.at[2]);
    const rb = joint(body, at, `rider${i + 1}`);
    const R = rb.abs;
    // Each is built at the origin facing +Z and lying on the slope, then set at its hold.
    const fig = new THREE.Group();
    R.add(fig);
    fig.position.copy(at);
    const { n } = domeAt(Math.atan2(at.x, at.z), Math.asin(Math.min(1, (at.y - DOME.y0) / DOME.b)));
    fig.quaternion.setFromUnitVectors(UP, n);
    fig.rotateY(r.face);
    fig.scale.setScalar(1.3);
    if (r.kind === 'goblin') {
      // A hunched trasgo, flat to the shell: a lump of a body, a big head, long ears, arms reaching to grip.
      k4.swell(fig, ink, 0, 0.14, 0, 0.14, 0.16, 0.12, 6, 4).rotation.x = 0.7;
      k4.swell(fig, ink, 0, 0.28, 0.12, 0.1, 0.09, 0.1, 6, 4);
      for (const k of [-1, 1]) {
        k4.shard(fig, ink, V(k * 0.08, 0.32, 0.1), V(k, 0.5, -0.4), 0.17, 0.03);
        limb(fig, V(k * 0.1, 0.2, 0.06), V(k * 0.22, 0.04, 0.2), 0.035, 0.025, ink, 4);
        limb(fig, V(k * 0.08, 0.06, -0.08), V(k * 0.18, 0.02, -0.2), 0.04, 0.03, ink, 4);
        M(new THREE.SphereGeometry(0.018, 4, 3), eye, fig).position.set(k * 0.035, 0.3, 0.2);
      }
      // A notched blade on its back.
      limb(fig, V(0.05, 0.2, -0.1), V(-0.12, 0.4, -0.2), 0.012, 0.02, bone, 3);
    } else {
      // A skeleton hauling itself up: ribs, a skull, long bare arms over the plates.
      limb(fig, V(0, 0.02, -0.06), V(0, 0.3, 0.06), 0.022, 0.022, bone, 4);
      for (let j = 0; j < 3; j++) {
        const rib = M(new THREE.TorusGeometry(0.07 - j * 0.008, 0.012, 3, 7, Math.PI * 1.4), bone, fig);
        rib.position.set(0, 0.14 + j * 0.06, 0.02 + j * 0.02);
        rib.rotation.set(Math.PI / 2 + 0.4, 0, Math.PI * 0.8);
      }
      const skull = k4.swell(fig, bone, 0, 0.38, 0.1, 0.075, 0.08, 0.085, 6, 4);
      skull.rotation.x = 0.3;
      for (const k of [-1, 1]) {
        M(new THREE.SphereGeometry(0.02, 4, 3), ink, fig).position.set(k * 0.03, 0.39, 0.17);
        M(new THREE.SphereGeometry(0.012, 4, 3), eye, fig).position.set(k * 0.03, 0.39, 0.18);
        limb(fig, V(k * 0.07, 0.28, 0.06), V(k * 0.2, 0.18, 0.24), 0.016, 0.014, bone, 3);
        limb(fig, V(k * 0.2, 0.18, 0.24), V(k * 0.24, 0.03, 0.34), 0.014, 0.01, bone, 3);
        limb(fig, V(k * 0.03, 0.02, -0.06), V(k * 0.14, -0.02, -0.28), 0.018, 0.014, bone, 3);
      }
    }
    // Their eyes, small violet points (glows ride the rider's bone).
    fig.updateMatrix();
    glowSprite(R, '#b48cff', 0.2, at.clone().add(V(0, 0.42, 0).applyQuaternion(fig.quaternion)), 0.85);
    riders[`rider${i + 1}`] = rb.p;
  });

  return done(c, SHELLBACK_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, head: headB.p, ...legs, ...riders,
  }));
};
